import NotesHistoryButton from '../../components/NotesHistoryButton';
import {
  Alert, Autocomplete, Box, Button, Checkbox, CircularProgress, FormControl, FormControlLabel,
  IconButton, InputLabel, MenuItem, Select, SelectChangeEvent, TextField, Typography,
} from '@mui/material';
import { Add, Delete, DragIndicator } from '@mui/icons-material';
import { Formik, FormikProps } from 'formik';
import * as Yup from 'yup';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FormikTextField, FormikTagSelect } from '../../components/FormikFields';
import { useAuthStore } from '../../stores/auth.store';
import FormDirtyTracker from '../../components/FormDirtyTracker';
import {
  trafficSourcesService, TrafficSource, TrafficSourceConfig, TrafficSourceCustomParam,
} from '../../services/traffic-sources.service';
import { partnersService, Partner } from '../../services/partners.service';
import { EntitySelectFieldFormik } from '../../components/EntitySelectField';
import { WorkspaceField, defaultCreateWorkspace } from '../../components/WorkspaceField';

const EMPTY_EXT_ID = { ts_name: '', ts_token: '' };

const DEFAULT_CONFIG: TrafficSourceConfig = {
  params: {
    external_ids: [{ ...EMPTY_EXT_ID }],
    cost: { ts_name: '', ts_token: '' },
    custom: [],
  },
  currency: { code: 'USD' },
};

interface TrafficSourceFormValues {
  workspace_id: string;
  name: string;
  kind: string;
  notes: string;
  ref: string;
  config: TrafficSourceConfig;
  domains: string[];
  postback_id: string | null;
  partner_id: string | null;
  revshare_pct: string;
  tag_ids: string[];
}

const schema = Yup.object({
  name: Yup.string().trim().required('Name is required'),
});

// ── Sortable param row ──

function SortableParamRow({ param, onChange, onRemove }: {
  param: TrafficSourceCustomParam;
  onChange: (field: keyof TrafficSourceCustomParam, value: string | number) => void;
  onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: param.index });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box ref={setNodeRef} style={style} sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
      <IconButton size="small" sx={{ cursor: 'grab', p: 0.3, touchAction: 'none' }} {...attributes} {...listeners}>
        <DragIndicator sx={{ fontSize: 18, color: 'text.secondary' }} />
      </IconButton>
      <FormControl size="small" sx={{ width: 65 }}>
        <InputLabel>V#</InputLabel>
        <Select
          value={String(param.index)}
          label="V#"
          onChange={(e: SelectChangeEvent) => onChange('index', Number(e.target.value))}
        >
          {Array.from({ length: 10 }, (_, i) => i + 1).map(n => (
            <MenuItem key={n} value={String(n)}>{n}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField size="small" label="Name" value={param.name}
        onChange={(e) => onChange('name', e.target.value)} sx={{ flex: 1 }} />
      <TextField size="small" label="Param" value={param.ts_name}
        onChange={(e) => onChange('ts_name', e.target.value)} sx={{ flex: 1 }} />
      <TextField size="small" label="Token" value={param.ts_token || ''}
        onChange={(e) => onChange('ts_token', e.target.value)} sx={{ flex: 1 }} />
      <Checkbox size="small" checked={param.in_url !== false} title="Include in URL"
        onChange={(e) => onChange('in_url' as any, e.target.checked as any)} sx={{ p: 0.3 }} />
      <IconButton size="small" color="error" onClick={onRemove} sx={{ p: 0.3 }}>
        <Delete sx={{ fontSize: 16 }} />
      </IconButton>
    </Box>
  );
}

// ── Config editor ──

function ConfigEditor({ config, setConfig }: { config: TrafficSourceConfig; setConfig: (c: TrafficSourceConfig) => void }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleAddCustomParam = () => {
    const usedIndices = new Set(config.params.custom.map(p => p.index));
    let nextIndex = 1;
    while (usedIndices.has(nextIndex) && nextIndex <= 10) nextIndex++;
    if (nextIndex > 10) return;
    setConfig({
      ...config,
      params: {
        ...config.params,
        custom: [...config.params.custom, { index: nextIndex, name: '', ts_name: '', ts_token: '' }],
      },
    });
  };

  const handleRemoveCustomParam = (index: number) => {
    setConfig({
      ...config,
      params: { ...config.params, custom: config.params.custom.filter(p => p.index !== index) },
    });
  };

  const handleCustomParamChange = (index: number, field: keyof TrafficSourceCustomParam, value: string | number) => {
    setConfig({
      ...config,
      params: {
        ...config.params,
        custom: config.params.custom.map(p => p.index === index ? { ...p, [field]: value } : p),
      },
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = config.params.custom.findIndex(p => p.index === active.id);
    const newIndex = config.params.custom.findIndex(p => p.index === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    setConfig({
      ...config,
      params: { ...config.params, custom: arrayMove(config.params.custom, oldIndex, newIndex) },
    });
  };

  const extIds = config.params.external_ids || [];
  // A minimal/legacy config may omit these; reading .ts_name/.code off undefined crashes the whole page.
  const cost = config.params.cost ?? { ts_name: '', ts_token: '' };
  const currencyCode = config.currency?.code ?? '';

  const updateExtId = (idx: number, field: string, value: string | boolean) => {
    const updated = [...extIds];
    updated[idx] = { ...updated[idx], [field]: value };
    setConfig({ ...config, params: { ...config.params, external_ids: updated } });
  };

  const addExtId = () => {
    if (extIds.length >= 3) return;
    setConfig({ ...config, params: { ...config.params, external_ids: [...extIds, { ...EMPTY_EXT_ID }] } });
  };

  const removeExtId = (idx: number) => {
    const updated = extIds.filter((_, i) => i !== idx);
    setConfig({ ...config, params: { ...config.params, external_ids: updated.length ? updated : [{ ...EMPTY_EXT_ID }] } });
  };

  return (
    <>
      <Typography variant="subtitle2" sx={{ mt: 1 }}>Param Mapping</Typography>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          External ID — first matched param → external_id
        </Typography>
        <Button size="small" startIcon={<Add />} onClick={addExtId} disabled={extIds.length >= 3}>
          Add
        </Button>
      </Box>
      {extIds.map((cid, idx) => (
        <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            label={`External ID ${idx + 1} param`} placeholder="e.g. gclid" size="small"
            value={cid.ts_name}
            onChange={(e) => updateExtId(idx, 'ts_name', e.target.value)}
            sx={{ flex: 1 }}
          />
          <TextField
            label="Token" placeholder="e.g. {gclid}" size="small"
            value={cid.ts_token || ''}
            onChange={(e) => updateExtId(idx, 'ts_token', e.target.value)}
            sx={{ flex: 1 }}
          />
          <FormControlLabel
            control={<Checkbox size="small" checked={cid.in_url !== false}
              onChange={(e) => updateExtId(idx, 'in_url', e.target.checked)} />}
            label={<Typography variant="caption">URL</Typography>}
            sx={{ ml: 0, mr: 0 }}
          />
          {extIds.length > 1 && (
            <IconButton size="small" color="error" onClick={() => removeExtId(idx)} sx={{ p: 0.3 }}>
              <Delete sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        </Box>
      ))}

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <TextField
          label="Cost param" placeholder="e.g. cost" size="small"
          value={cost.ts_name}
          onChange={(e) => setConfig({
            ...config,
            params: { ...config.params, cost: { ...cost, ts_name: e.target.value } },
          })}
          sx={{ flex: 1 }}
        />
        <TextField
          label="Multiply by" type="number" size="small"
          value={cost.multiply_by ?? ''}
          onChange={(e) => setConfig({
            ...config,
            params: {
              ...config.params,
              cost: { ...cost, multiply_by: e.target.value ? Number(e.target.value) : undefined },
            },
          })}
          sx={{ width: 130 }}
        />
      </Box>

      <TextField
        label="Currency Code" size="small"
        value={currencyCode}
        onChange={(e) => setConfig({ ...config, currency: { code: e.target.value } })}
        sx={{ width: 120 }}
      />

      {/* A picker, not free text: it must equal one of this source's params, and a
          mismatch silently stops cost attaching to clicks. */}
      <TextField
        select label="Cost grouping parameter" size="small" fullWidth
        value={config.params.cost_grouping_param || ''}
        onChange={(e) => setConfig({
          ...config,
          params: { ...config.params, cost_grouping_param: e.target.value || undefined },
        })}
        helperText="Which of this source's params cost is grouped by. The campaign id on every source today."
      >
        <MenuItem value=""><em>Default (campaign_id)</em></MenuItem>
        {(config.params.custom || [])
          .filter((c) => !!c.ts_name)
          .map((c) => (
            <MenuItem key={c.ts_name} value={c.ts_name}>
              {c.ts_name}{c.name ? ` — ${c.name}` : ''}
            </MenuItem>
          ))}
        {/* A stored name whose param was since renamed or removed still governs cost
            attribution, so show it rather than rendering blank — which reads as the default. */}
        {config.params.cost_grouping_param
          && !(config.params.custom || []).some((c) => c.ts_name === config.params.cost_grouping_param) && (
          <MenuItem value={config.params.cost_grouping_param}>
            {config.params.cost_grouping_param} — no longer a param on this source
          </MenuItem>
        )}
      </TextField>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
        <Typography variant="subtitle2">Custom Params</Typography>
        <Button size="small" startIcon={<Add />} onClick={handleAddCustomParam}
          disabled={config.params.custom.length >= 10}>
          Add
        </Button>
      </Box>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={config.params.custom.map(p => p.index)} strategy={verticalListSortingStrategy}>
          {config.params.custom.map((p) => (
            <SortableParamRow
              key={p.index}
              param={p}
              onChange={(field, value) => handleCustomParamChange(p.index, field, value)}
              onRemove={() => handleRemoveCustomParam(p.index)}
            />
          ))}
        </SortableContext>
      </DndContext>
    </>
  );
}


function toInitialValues(entity?: TrafficSource, duplicate = false): TrafficSourceFormValues {
  const cfg = entity?.config || DEFAULT_CONFIG;
  return {
    // A duplicate is a create: no preselection, like a new one (#297).
    workspace_id: duplicate ? defaultCreateWorkspace() : (entity as any)?.workspace_id ?? defaultCreateWorkspace(),
    name: entity ? (duplicate ? `${entity.name} (copy)` : entity.name) : '',
    kind: entity?.kind ?? '',
    notes: entity?.notes ?? '',
    ref: entity && !duplicate ? (entity.ref ?? '') : '',
    config: cfg.params.external_ids?.length
      ? cfg
      : { ...cfg, params: { ...cfg.params, external_ids: [{ ...EMPTY_EXT_ID }] } },
    domains: entity?.domains || [],
    postback_id: entity?.postback_id ?? null,
    partner_id: entity?.partner_id ?? null,
    revshare_pct: entity?.revshare_pct != null ? String(entity.revshare_pct) : '',
    tag_ids: entity?.tag_ids ?? [],
  };
}

export default function TrafficSourceForm({ trafficSource, onSaved, duplicate = false, onDirtyChange }: {
  trafficSource?: TrafficSource; onSaved?: (ts: TrafficSource) => void; duplicate?: boolean;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = !!trafficSource && !duplicate;
  // Writes are admin-only server-side (requireAdmin); mirror that here so a media buyer sees it read-only.
  const canEdit = useAuthStore((s) => s.user?.role === 'admin');

  const { data: partners } = useQuery({
    queryKey: ['partners'],
    queryFn: () => partnersService.list(),
  });
  const { data: kindOptions = [] } = useQuery({ queryKey: ['ts-kinds'], queryFn: trafficSourcesService.getKinds });

  return (
    <Formik
      initialValues={toInitialValues(trafficSource, duplicate)}
      enableReinitialize
      validationSchema={schema}
      onSubmit={async (values, { setSubmitting, setStatus }) => {
        try {
          const filteredExtIds = (values.config.params.external_ids || []).filter(c => c.ts_name);
          const saveConfig: TrafficSourceConfig = {
            ...values.config,
            params: {
              ...values.config.params,
              external_ids: filteredExtIds.length ? filteredExtIds : undefined,
            },
          };
          const payload = {
            workspace_id: values.workspace_id,
            name: values.name,
            kind: values.kind,
            notes: values.notes,
            ref: values.ref,
            domains: values.domains.filter(d => d.trim()),
            postback_id: values.postback_id,
            partner_id: values.partner_id,
            revshare_pct: values.revshare_pct ? Number(values.revshare_pct) : null,
            config: saveConfig,
            tag_ids: values.tag_ids,
          };
          const saved = isEdit
            ? await trafficSourcesService.update(trafficSource!.id, payload)
            : await trafficSourcesService.create(payload);
          queryClient.invalidateQueries({ queryKey: ['traffic-sources'] });
          if (isEdit) queryClient.invalidateQueries({ queryKey: ['traffic-source', trafficSource!.id] });
          enqueueSnackbar('Saved', { variant: 'success' });
          onSaved?.(saved);
        } catch (err: any) {
          setStatus(err?.response?.data?.error?.message || err.message || 'Save failed');
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ submitForm, isSubmitting, status, values, setFieldValue }: FormikProps<TrafficSourceFormValues>) => (
        <>
          {!canEdit && <Alert severity="info" sx={{ mb: 2, maxWidth: 1000 }}>Read-only — only admins can edit traffic sources.</Alert>}
          <Box
          component="form"
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            columnGap: 2, rowGap: 2, alignItems: 'start', maxWidth: 1000,
            ...(!canEdit && { pointerEvents: 'none', opacity: 0.6 }),
          }}
        >
          {onDirtyChange && <FormDirtyTracker onDirtyChange={onDirtyChange} />}
          <Box sx={{ gridColumn: '1 / -1', display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
            <FormikTextField name="name" label="Name" size="small" sx={{ flex: 1 }} />
            <FormikTextField name="ref" label="Ref" size="small" placeholder="e.g. T1200" sx={{ width: { xs: '100%', sm: 160 } }} />
          </Box>
          <Box sx={{ gridColumn: '1 / -1' }}>
            <Autocomplete
              freeSolo fullWidth options={kindOptions} disabled={!canEdit}
              value={values.kind || null}
              onChange={(_, v) => setFieldValue('kind', v ?? '')}
              inputValue={values.kind}
              onInputChange={(_, v) => setFieldValue('kind', v)}
              renderInput={(params) => (
                <TextField {...params} label="Kind (optional)" size="small"
                  placeholder="e.g. Taboola — groups this source with its copies in other workspaces" />
              )}
            />
          </Box>
          {!isEdit && (
            <Box sx={{ gridColumn: '1 / -1' }}>
              <WorkspaceField
                value={values.workspace_id}
                onChange={(id) => {
                  if (id !== values.workspace_id) {
                    // Postback, partner and tags live in the source workspace — they can't follow.
                    setFieldValue('postback_id', null);
                    setFieldValue('partner_id', null);
                    setFieldValue('tag_ids', []);
                  }
                  setFieldValue('workspace_id', id);
                }}
              />
            </Box>
          )}
          <Box sx={{ gridColumn: '1 / -1' }}>
            <FormikTextField name="notes" label="Notes" fullWidth size="small" multiline rows={2}
              slotProps={{ input: { endAdornment: <NotesHistoryButton entity="traffic-source" id={isEdit ? trafficSource?.id : undefined} /> } }} />
          </Box>
          <EntitySelectFieldFormik
            name="postback_id" field="postback_id" label="Postback (optional)"
          />
          <Box sx={{ gridColumn: '1 / -1' }}>
            <Autocomplete
              multiple freeSolo size="small" fullWidth
              options={[]}
              value={values.domains}
              onChange={(_, v) => setFieldValue('domains', v.map(d => d.trim()).filter(Boolean))}
              renderInput={(params) => (
                <TextField {...params} label="Domains" placeholder="e.g. track.example.com"
                  helperText="Custom tracking domains. Press Enter to add." size="small" />
              )}
            />
          </Box>
          <Box sx={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', gap: 2 }}>
            <ConfigEditor config={values.config} setConfig={(c) => setFieldValue('config', c)} />
          </Box>
          <Box sx={{ gridColumn: '1 / -1', display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <Autocomplete
              size="small"
              sx={{ flex: 1, maxWidth: 480 }}
              options={(partners || []).filter((p: Partner) => p.status === 'active')}
              getOptionLabel={(o: Partner) => o.name}
              value={(partners || []).find((p: Partner) => p.id === values.partner_id) || null}
              onChange={(_, v) => {
                setFieldValue('partner_id', v?.id || null);
                if (!v) setFieldValue('revshare_pct', '');
              }}
              renderInput={(params) => <TextField {...params} label="Partner (optional)" size="small" />}
            />
            {values.partner_id && (
              <FormikTextField
                name="revshare_pct"
                size="small" label="Revshare %" type="number"
                inputProps={{ min: 0, max: 100, step: 1 }}
                sx={{ width: 120 }}
              />
            )}
          </Box>
          <Box sx={{ gridColumn: '1 / -1' }}>
            <FormikTagSelect name="tag_ids" />
          </Box>
          {status && <Alert severity="error" sx={{ gridColumn: '1 / -1' }}>{status}</Alert>}
          {canEdit && (
            <Button variant="contained" onClick={submitForm} disabled={isSubmitting || !values.workspace_id} sx={{ gridColumn: '1 / -1', justifySelf: 'flex-start' }}>
              {isSubmitting ? <CircularProgress size={20} /> : 'Save'}
            </Button>
          )}
        </Box>
        </>
      )}
    </Formik>
  );
}
